/**
 * Mat Selector panel component
 */

import { useState, useMemo } from 'react';
import { Slider, CollapsibleCard, Switch, cn } from '@frameit/ui';
import { useProjectStore, useSelectedArtwork } from '../../stores/projectStore';
import { useIsMobile } from '../../hooks/useIsMobile';
import { MATS } from '../../data/mats';

export interface MatSelectorProps {
  /** Custom class name */
  className?: string;
}

/** Mat selector component */
export function MatSelector({ className }: MatSelectorProps) {
  const selectedArtwork = useSelectedArtwork();
  const updateMat = useProjectStore((s) => s.updateMat);
  const updateDisplayScale = useProjectStore((s) => s.updateDisplayScale);

  const [selectedPreset, setSelectedPreset] = useState<string>(
    selectedArtwork?.mat?.templateId || 'mat-white',
  );
  const [search, setSearch] = useState('');
  const isMobile = useIsMobile();

  // Filter mats by search
  const filteredMats = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return MATS;
    return MATS.filter((m) => m.name.toLowerCase().includes(q) || m.category.includes(q));
  }, [search]);

  // Check if mat is enabled
  const matEnabled = !!selectedArtwork?.mat;

  // Handle mat toggle
  const handleToggle = (enabled: boolean) => {
    if (!selectedArtwork) return;

    if (enabled) {
      // Enable mat with default settings
      updateMat(selectedArtwork.id, {
        templateId: selectedPreset,
        width: 0.1, // 10% of artwork size
      });
    } else {
      // Disable mat
      updateMat(selectedArtwork.id, null);
    }
  };

  // Handle preset selection
  const handlePresetSelect = (presetId: string) => {
    if (!selectedArtwork) return;
    setSelectedPreset(presetId);

    if (matEnabled) {
      updateMat(selectedArtwork.id, { templateId: presetId });
    }
  };

  // Handle width change
  const handleWidthChange = (width: number) => {
    if (!selectedArtwork || !matEnabled) return;
    updateMat(selectedArtwork.id, { width });
  };

  // Handle color adjustments
  const handleColorAdjustment = (key: 'hue' | 'saturation' | 'lightness', value: number) => {
    if (!selectedArtwork || !matEnabled) return;
    updateMat(selectedArtwork.id, {
      colorAdjustments: {
        ...selectedArtwork.mat?.colorAdjustments,
        [key]: value,
      },
    });
  };

  // Handle layer count change
  const handleLayerChange = (layers: number) => {
    if (!selectedArtwork || !matEnabled) return;
    updateMat(selectedArtwork.id, { layers });
  };

  // Handle bevel color change
  const handleBevelColorChange = (color: string) => {
    if (!selectedArtwork || !matEnabled) return;
    updateMat(selectedArtwork.id, { bevelColor: color });
  };

  // Handle second layer template change
  const handleSecondLayerSelect = (templateId: string) => {
    if (!selectedArtwork || !matEnabled) return;
    updateMat(selectedArtwork.id, { secondLayerTemplateId: templateId });
  };

  if (!selectedArtwork) {
    return (
      <div className={cn('p-4 text-center text-muted-foreground', className)}>
        Select an artwork to customize its mat
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Mat toggle */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold">Mat Board</h3>
            <p className="text-xs text-muted-foreground">
              Add a decorative border around your artwork
            </p>
          </div>
          <Switch checked={matEnabled} onCheckedChange={handleToggle} />
        </div>
      </div>

      {/* Mat options + controls in single scroll container */}
      <div className={cn('flex-1 overflow-auto', !matEnabled && 'opacity-50 pointer-events-none')}>
        <div className="p-4">
          <h4 className="text-sm font-medium mb-3">Mat Color</h4>

          {/* Search */}
          <div className="relative mb-3">
            <svg
              className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search mats..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-8 pl-8 pr-3 text-sm rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          {/* Color grid */}
          <div className="grid grid-cols-4 gap-2 mb-6">
            {filteredMats.map((preset) => (
              <button
                key={preset.id}
                onClick={() => handlePresetSelect(preset.id)}
                className={cn(
                  'aspect-square rounded-lg border-2 transition-all',
                  'hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary',
                  selectedPreset === preset.id
                    ? 'border-primary ring-2 ring-primary/20'
                    : 'border-transparent',
                )}
                style={{ backgroundColor: preset.color }}
                title={preset.name}
              >
                {selectedPreset === preset.id && (
                  <svg
                    className={cn(
                      'w-4 h-4 mx-auto',
                      ['dark', 'color'].includes(preset.category) &&
                        preset.color.length === 7 &&
                        parseInt(preset.color.slice(1), 16) < 0x808080
                        ? 'text-white'
                        : 'text-primary',
                    )}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </button>
            ))}
          </div>

          {/* Mat name display */}
          <p className="text-sm text-center text-muted-foreground mb-6">
            {MATS.find((p) => p.id === selectedPreset)?.name || 'Custom'}
          </p>
        </div>

        {/* Mat controls */}
        <CollapsibleCard
          title="Mat Settings"
          collapsible={isMobile}
          className="mx-4 mb-4"
          disabled={!matEnabled}
        >
          {/* Artwork size */}
          <div>
            <label className="text-xs text-muted-foreground">Artwork Size</label>
            <Slider
              value={selectedArtwork.displayScale ?? 1}
              min={0.5}
              max={2}
              step={0.05}
              onChange={(value) => updateDisplayScale(selectedArtwork.id, value)}
              showValue
              formatValue={(v) => `${Math.round(v * 100)}%`}
            />
          </div>

          {/* Mat width */}
          <div>
            <label className="text-xs text-muted-foreground">Mat Width</label>
            <Slider
              value={selectedArtwork.mat?.width ?? 0.1}
              min={0.02}
              max={0.3}
              step={0.01}
              onChange={handleWidthChange}
              showValue
              formatValue={(v) => `${Math.round(v * 100)}%`}
            />
          </div>

          {/* Mat layers */}
          <div>
            <label className="text-xs text-muted-foreground mb-2 block">Layers</label>
            <div className="flex gap-1">
              {([1, 2, 3] as const).map((count) => (
                <button
                  key={count}
                  onClick={() => handleLayerChange(count)}
                  className={cn(
                    'flex-1 py-1.5 text-xs rounded-md border transition-colors',
                    (selectedArtwork.mat?.layers ?? 1) === count
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background border-input hover:bg-accent',
                  )}
                >
                  {count === 1 ? 'Single' : count === 2 ? 'Double' : 'Triple'}
                </button>
              ))}
            </div>
          </div>

          {/* Bevel color - shown for double/triple mat */}
          {(selectedArtwork.mat?.layers ?? 1) > 1 && (
            <>
              <div>
                <label className="text-xs text-muted-foreground mb-2 block">Bevel Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={selectedArtwork.mat?.bevelColor ?? '#f5f0e6'}
                    onChange={(e) => handleBevelColorChange(e.target.value)}
                    className="w-8 h-8 rounded border cursor-pointer"
                  />
                  <span className="text-xs text-muted-foreground">Core reveal between layers</span>
                </div>
              </div>

              {/* Second layer color */}
              <div>
                <label className="text-xs text-muted-foreground mb-2 block">
                  Inner Layer Color
                </label>
                <div className="grid grid-cols-6 gap-1">
                  {MATS.slice(0, 12).map((mat) => (
                    <button
                      key={mat.id}
                      onClick={() => handleSecondLayerSelect(mat.id)}
                      className={cn(
                        'w-full aspect-square rounded border transition-all',
                        (selectedArtwork.mat?.secondLayerTemplateId ?? selectedPreset) === mat.id
                          ? 'border-primary ring-1 ring-primary/30'
                          : 'border-transparent hover:border-input',
                      )}
                      style={{ backgroundColor: mat.color }}
                      title={mat.name}
                    />
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Hue adjustment */}
          <div>
            <label className="text-xs text-muted-foreground">Hue Shift</label>
            <Slider
              value={selectedArtwork.mat?.colorAdjustments?.hue ?? 0}
              min={-180}
              max={180}
              step={1}
              onChange={(v) => handleColorAdjustment('hue', v)}
              showValue
              formatValue={(v) => `${v}°`}
            />
          </div>

          {/* Saturation adjustment */}
          <div>
            <label className="text-xs text-muted-foreground">Saturation</label>
            <Slider
              value={selectedArtwork.mat?.colorAdjustments?.saturation ?? 0}
              min={-100}
              max={100}
              step={1}
              onChange={(v) => handleColorAdjustment('saturation', v)}
              showValue
              formatValue={(v) => `${v > 0 ? '+' : ''}${v}%`}
            />
          </div>

          {/* Lightness adjustment */}
          <div>
            <label className="text-xs text-muted-foreground">Lightness</label>
            <Slider
              value={selectedArtwork.mat?.colorAdjustments?.lightness ?? 0}
              min={-100}
              max={100}
              step={1}
              onChange={(v) => handleColorAdjustment('lightness', v)}
              showValue
              formatValue={(v) => `${v > 0 ? '+' : ''}${v}%`}
            />
          </div>
        </CollapsibleCard>
      </div>
    </div>
  );
}
